import React, { useEffect, useMemo, useState } from 'react';
import cl from 'classnames';
import { HOTELS, HOTELS_VIEW_MODE } from 'src/constants';
import { getLocalStorageItem } from 'src/helper';
import { useContextData, useLanguage, usePage } from 'src/hooks';
import { useAccountState } from 'src/services/auth';
import {
  usePaginator,
  Link,
  useSearchInput,
  FilterModal,
  FilterModalButton,
  FilterModalIndicator,
} from 'src/components/general';
import { ReactComponent as Crown } from 'src/icons/crown.svg';
import ViewModeSwitcher, { ViewMode } from './viewModeSwitcher';
import styles from './hotelsPageStyle.module.scss';

export const HotelsPage = () => {
  const { user } = useAccountState();
  const {
    access: { accessMap },
  } = usePage();
  const { t } = useLanguage();
  const {
    layoutScroller: { scroll },
  } = useContextData();
  const { currentPage, countPerPage, paginator } = usePaginator({
    countsPerPages: [20, 40, 60],
    dataLength: user?.companies.length || 0,
    initPerPage: 60,
  });

  const [viewMode, setViewMode] = useState<ViewMode>(
    getLocalStorageItem<ViewMode>(HOTELS_VIEW_MODE, 'list'),
  );
  const [showSearchModal, setShowSearchModal] = useState(false);

  const {
    searchString,
    searchInput,
    modalSearchInput,
    applySearch,
    cancelSearch,
    clearSearch,
    focusInput,
  } = useSearchInput({
    inputClassName: cl(styles.searchInput, viewMode === 'list' && styles.long),
    modalInputClassName: styles.modalSearchInput,
    onEnter: () => {
      setShowSearchModal(false);
    },
    isMiddle: true,
  });

  const search = useMemo(
    () => searchString.trim().toLowerCase(),
    [searchString],
  );

  useEffect(() => {
    if (showSearchModal) {
      focusInput();
    }
  }, [showSearchModal, focusInput]);

  useEffect(() => {
    scroll(0, 0);
  }, [currentPage, scroll]);

  const list = useMemo(() => {
    if (!user?.companies) {
      return [];
    }

    const startIndex = (currentPage - 1) * countPerPage;
    const endIndex = currentPage * countPerPage;

    return user.companies
      .filter(({ name }) => !search || name.toLowerCase().includes(search))
      .slice(startIndex, endIndex);
  }, [user?.companies, countPerPage, currentPage, search]);

  if (!user) {
    return null;
  }

  return (
    <div className={styles.container}>
      <FilterModal
        show={showSearchModal}
        close={() => {
          setShowSearchModal(false);
        }}
        onApply={() => {
          applySearch();
          setShowSearchModal(false);
        }}
        onCancel={() => {
          cancelSearch();
          setShowSearchModal(false);
        }}
        isSearch
      >
        {modalSearchInput}
      </FilterModal>
      <div className={styles.topSection}>
        <ViewModeSwitcher viewMode={viewMode} setViewMode={setViewMode} />
        {searchInput}
        <div className={styles.filterButtons}>
          <FilterModalButton
            setShow={setShowSearchModal}
            icon="search"
            badge={Boolean(search)}
          />
          <FilterModalIndicator
            icon="search"
            text={search}
            clear={clearSearch}
          />
        </div>
      </div>
      {viewMode === 'cards' ? (
        <div className={styles.cards}>
          {list.map(({ name, id, image, todayPaymentCount }) => (
            <Link
              key={`card-${id}`}
              className={styles.card}
              src={`/${HOTELS}/${id}/${accessMap[HOTELS][0]}`}
            >
              <div
                className={styles.image}
                style={{
                  background: image
                    ? `center / cover no-repeat url("${image}")`
                    : '#F4F5F5',
                }}
              >
                {!image && (
                  <svg
                    width="37"
                    height="36"
                    viewBox="0 0 37 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M33.8008 28.5C33.8008 29.2956 33.4847 30.0587 32.9221 30.6213C32.3595 31.1839 31.5964 31.5 30.8008 31.5H6.80078C6.00513 31.5 5.24207 31.1839 4.67946 30.6213C4.11685 30.0587 3.80078 29.2956 3.80078 28.5V7.5C3.80078 6.70435 4.11685 5.94129 4.67946 5.37868C5.24207 4.81607 6.00513 4.5 6.80078 4.5H14.3008L17.3008 9H30.8008C31.5964 9 32.3595 9.31607 32.9221 9.87868C33.4847 10.4413 33.8008 11.2044 33.8008 12V28.5Z"
                      stroke="#99A9BB"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {todayPaymentCount > 0 && (
                  <div className={styles.count}>
                    <Crown />
                    {todayPaymentCount}
                  </div>
                )}
              </div>
              <div className={styles.name}>{name}</div>
            </Link>
          ))}
        </div>
      ) : (
        <div className={styles.list}>
          {list.map(({ name, id, image, todayPaymentCount }) => (
            <Link
              key={`item-${id}`}
              className={styles.item}
              src={`/${HOTELS}/${id}/${accessMap[HOTELS][0]}`}
            >
              <div
                className={styles.image}
                style={{
                  background: image
                    ? `center / cover no-repeat url("${image}")`
                    : '#F4F5F5',
                }}
              >
                {!image && (
                  <svg
                    width="23"
                    height="23"
                    viewBox="0 0 23 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21.0837 18.2083C21.0837 18.7167 20.8817 19.2042 20.5223 19.5636C20.1628 19.9231 19.6753 20.125 19.167 20.125H3.83366C3.32533 20.125 2.83782 19.9231 2.47837 19.5636C2.11893 19.2042 1.91699 18.7167 1.91699 18.2083V4.79167C1.91699 4.28334 2.11893 3.79582 2.47837 3.43638C2.83782 3.07693 3.32533 2.875 3.83366 2.875H8.62533L10.542 5.75H19.167C19.6753 5.75 20.1628 5.95193 20.5223 6.31138C20.8817 6.67082 21.0837 7.15833 21.0837 7.66667V18.2083Z"
                      stroke="#99A9BB"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <div className={styles.name}>{name}</div>
              {todayPaymentCount > 0 && (
                <div className={styles.count}>
                  <Crown />
                  {todayPaymentCount}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
      {search && !list.length ? (
        <div className={styles.notFound}>{t('general.notFound')}</div>
      ) : (
        <div className={styles.paginatorContainer}>{paginator}</div>
      )}
    </div>
  );
};

export default HotelsPage;
